import type { OrderStatus } from "@/types/tms";

/**
 * Manual status promotions available as explicit order-page actions.
 *
 * Phase 4 of the confirm-order rework: the edit form no longer carries a
 * status dropdown. Confirming lives in `useConfirmOrder`, cancelling in
 * `useOrderCancellation`, and the two remaining forward moves — In Transit
 * and Delivered — are decided here so the button gating and the write path
 * can never disagree.
 */
export type ManualStatusTarget = "in_transit" | "delivered";

export interface StatusTransitionInput {
  /** Live status of the order being promoted. */
  currentStatus: unknown;
  /** Caller holds the admin role. */
  isAdmin: boolean;
  /** Existing write-once actual timestamps (ISO strings) if already stamped. */
  actualDepartureTime?: string | null;
  actualArrivalTime?: string | null;
  /** Clock injection for tests. */
  now?: Date;
}

export interface StatusTransitionPlan {
  allowed: boolean;
  /** Human reason shown in the disabled-action tooltip. Empty when allowed. */
  reason: string;
  /** Columns to write on `orders` when allowed. */
  patch: Record<string, string>;
}

/** Statuses a manual promotion may start from. */
const FROM_IN_TRANSIT: OrderStatus[] = ["confirmed", "tender_received"];
const FROM_DELIVERED: OrderStatus[] = ["confirmed", "tender_received", "in_transit"];

function blocked(reason: string): StatusTransitionPlan {
  return { allowed: false, reason, patch: {} };
}

/**
 * Decide whether a manual promotion is permitted and which columns it writes.
 *
 * Rules:
 *  - Terminal orders (`delivered`, `canceled`) never move.
 *  - `in_transit` is admin-only — dispatch departure normally comes from the
 *    driver app or the EAPI poller.
 *  - `delivered` is available to any writer once the order is confirmed.
 *  - Actual departure/arrival timestamps are write-once: only nulls are filled.
 */
export function planStatusTransition(
  target: ManualStatusTarget,
  input: StatusTransitionInput,
): StatusTransitionPlan {
  const current = String(input.currentStatus ?? "").trim().toLowerCase();

  if (current === "canceled") return blocked("This order is canceled.");
  if (current === "delivered") return blocked("This order is already delivered.");
  if (current === target) return blocked("The order is already in this status.");

  if (target === "in_transit") {
    if (!input.isAdmin) {
      return blocked("Only an administrator can mark an order in transit.");
    }
  }
  const normalFrom = target === "in_transit" ? FROM_IN_TRANSIT : FROM_DELIVERED;
  // Never skip confirmation: booked orders must be confirmed first, admin or not.
  if (!normalFrom.includes(current as OrderStatus)) {
    return blocked(
      target === "in_transit"
        ? "Confirm the order before marking it in transit."
        : "Confirm the order before marking it delivered.",
    );
  }

  const nowIso = (input.now ?? new Date()).toISOString();
  const patch: Record<string, string> = { status: target };
  if (!input.actualDepartureTime) patch.actual_departure_time = nowIso;
  if (target === "delivered" && !input.actualArrivalTime) {
    patch.actual_arrival_time = nowIso;
  }
  return { allowed: true, reason: "", patch };
}
